/**
 * Cart Drawer JavaScript - Enhanced Version
 * 增强版购物车侧边栏JavaScript
 * 新增功能：
 * 1. 空购物车时点击图标直接跳转到购物车页面
 * 2. 更好的错误处理和用户体验
 * 3. 完整的购物车状态管理
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById("cart-drawer");
    this.overlay = document.getElementById("cart-drawer-overlay");
    this.closeBtn = document.getElementById("cart-drawer-close");
    this.itemsContainer = document.getElementById("cart-drawer-items");
    this.subtotalElement = document.getElementById("cart-drawer-subtotal");
    this.noteTextarea = document.getElementById("cart-drawer-note");

    this.isOpen = false;
    this.isLoading = false;
    this.currentCart = null;

    // 添加调试信息
    console.log("CartDrawer initialized:", {
      drawer: this.drawer,
      overlay: this.overlay,
      closeBtn: this.closeBtn,
      itemsContainer: this.itemsContainer,
    });

    this.init();
  }

  init() {
    this.bindEvents();
    this.loadCartData();
  }

  async loadCartData() {
    try {
      const response = await fetch("/cart.js");
      if (response.ok) {
        this.currentCart = await response.json();
        console.log("Initial cart data loaded:", this.currentCart);
        this.updateCartCount(this.currentCart.item_count);
      }
    } catch (error) {
      console.error("Error loading initial cart data:", error);
    }
  }

  bindEvents() {
    // Open cart drawer when cart icon is clicked
    document.addEventListener("click", (e) => {
      // Look for cart trigger elements
      const cartTrigger =
        e.target.closest("[data-cart-drawer-trigger]") ||
        e.target.closest(".cart-icon") ||
        e.target.closest("#cart-icon-bubble") ||
        e.target.closest(".cart-drawer-button");

      if (cartTrigger) {
        e.preventDefault();
        e.stopPropagation();
        console.log("Cart trigger clicked");
        this.handleCartClick();
      }
    });

    // Close drawer events
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => {
        console.log("Close button clicked");
        this.close();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener("click", () => {
        console.log("Overlay clicked");
        this.close();
      });
    }

    // ESC key to close
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        console.log("ESC key pressed");
        this.close();
      }
    });

    // 使用事件委托处理动态内容的点击事件
    document.addEventListener("click", (e) => {
      // 减号按钮
      if (e.target.closest(".cart-drawer__quantity-minus")) {
        e.preventDefault();
        const btn = e.target.closest(".cart-drawer__quantity-minus");
        const variantId = btn.dataset.variantId;
        const currentQty = parseInt(btn.dataset.currentQuantity) || 1;
        const newQuantity = Math.max(0, currentQty - 1);
        console.log("Minus button clicked:", { variantId, currentQty, newQuantity });
        this.updateQuantity(variantId, newQuantity);
      }

      // 加号按钮
      if (e.target.closest(".cart-drawer__quantity-plus")) {
        e.preventDefault();
        const btn = e.target.closest(".cart-drawer__quantity-plus");
        const variantId = btn.dataset.variantId;
        const currentQty = parseInt(btn.dataset.currentQuantity) || 1;
        const newQuantity = currentQty + 1;
        console.log("Plus button clicked:", { variantId, currentQty, newQuantity });
        this.updateQuantity(variantId, newQuantity);
      }

      // 删除按钮
      if (e.target.closest(".cart-drawer__item-remove")) {
        e.preventDefault();
        const btn = e.target.closest(".cart-drawer__item-remove");
        const variantId = btn.dataset.variantId;
        console.log("Remove button clicked:", { variantId });
        this.removeItem(variantId);
      }

      // Check out 按钮
      if (e.target.closest(".cart-drawer__checkout")) {
        e.preventDefault();
        console.log("Checkout button clicked");
        window.location.href = "/checkout";
      }

      // View cart 按钮
      if (e.target.closest(".cart-drawer__view-cart")) {
        e.preventDefault();
        console.log("View cart button clicked");
        window.location.href = "/cart";
      }
    });

    // 数量输入框变化
    document.addEventListener("change", (e) => {
      if (e.target.classList.contains("cart-drawer__quantity-input")) {
        const input = e.target;
        const variantId = input.dataset.variantId;
        const newQuantity = parseInt(input.value) || 0;
        console.log("Quantity input changed:", { variantId, newQuantity });
        this.updateQuantity(variantId, newQuantity);
      }
    });

    // Cart note update
    if (this.noteTextarea) {
      this.noteTextarea.addEventListener("change", () => {
        this.updateNote(this.noteTextarea.value);
      });
    }
  }

  async handleCartClick() {
    console.log("Handling cart click...");

    // 首先获取最新的购物车数据
    try {
      const response = await fetch("/cart.js");
      if (response.ok) {
        const cart = await response.json();
        this.currentCart = cart;
        console.log("Current cart status:", cart);

        // 如果购物车为空，直接跳转到购物车页面
        if (cart.item_count === 0) {
          console.log("Cart is empty, redirecting to cart page");
          window.location.href = "/cart";
          return;
        }

        // 如果购物车有商品，打开侧边栏
        console.log("Cart has items, opening drawer");
        this.open();
      } else {
        console.error("Failed to fetch cart data");
        // 如果无法获取购物车数据，默认跳转到购物车页面
        window.location.href = "/cart";
      }
    } catch (error) {
      console.error("Error fetching cart data:", error);
      // 如果出现错误，默认跳转到购物车页面
      window.location.href = "/cart";
    }
  }

  async open() {
    if (this.isLoading) return;

    console.log("Opening cart drawer...");
    this.isLoading = true;

    try {
      // 先显示抽屉，再刷新内容
      if (this.overlay) {
        this.overlay.style.display = "block";
        setTimeout(() => this.overlay.classList.add("active"), 10);
      }

      if (this.drawer) {
        this.drawer.classList.add("active");
      }

      this.isOpen = true;
      document.body.style.overflow = "hidden";

      // 异步刷新购物车内容
      this.refreshCart().catch((error) => {
        console.error("Error refreshing cart on open:", error);
      });

      console.log("Cart drawer opened successfully");
    } catch (error) {
      console.error("Error opening cart drawer:", error);
    } finally {
      this.isLoading = false;
    }
  }

  close() {
    console.log("Closing cart drawer...");

    if (this.overlay) {
      this.overlay.classList.remove("active");
      setTimeout(() => {
        this.overlay.style.display = "none";
      }, 300);
    }

    if (this.drawer) {
      this.drawer.classList.remove("active");
    }

    this.isOpen = false;
    document.body.style.overflow = "";

    console.log("Cart drawer closed");
  }

  async refreshCart() {
    try {
      console.log("Refreshing cart...");
      const response = await fetch("/cart.js");

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();
      console.log("Cart data received:", cart);

      this.currentCart = cart;

      // 更新购物车内容
      await this.updateDrawerContent(cart);

      // 更新购物车数量
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error("Error refreshing cart:", error);
      this.showError("无法加载购物车内容，请刷新页面重试。");
    }
  }

  async updateDrawerContent(cart) {
    try {
      console.log("Updating drawer content...");

      if (!this.drawer) {
        console.error("Cart drawer element not found");
        return;
      }

      // 更新购物车项目
      if (this.itemsContainer) {
        this.itemsContainer.innerHTML = this.generateCartItemsHTML(cart.items);
      }

      // 更新小计
      if (this.subtotalElement) {
        this.subtotalElement.textContent = this.formatMoney(cart.total_price);
      }

      // 更新购物车数量显示
      const countElement = this.drawer.querySelector(".cart-drawer__count");
      if (countElement) {
        countElement.textContent = cart.item_count;
      }

      // 更新空购物车状态
      const emptyState = this.drawer.querySelector(".cart-drawer__empty");
      const content = this.drawer.querySelector(".cart-drawer__content");

      if (cart.items.length === 0) {
        if (emptyState) emptyState.style.display = "block";
        if (content) content.style.display = "none";
      } else {
        if (emptyState) emptyState.style.display = "none";
        if (content) content.style.display = "flex";
      }

      console.log("Drawer content updated successfully");
    } catch (error) {
      console.error("Error updating drawer content:", error);
    }
  }

  generateCartItemsHTML(items) {
    if (!items || items.length === 0) {
      return "<p class=\"cart-drawer__empty-message\">您的购物车是空的</p>";
    }

    return items
      .map(
        (item) => `
      <div class="cart-drawer__item" data-variant-id="${item.variant_id}">
        <div class="cart-drawer__item-image">
          <img src="${item.image || ""}" alt="${item.product_title}" loading="lazy">
        </div>
        <div class="cart-drawer__item-details">
          <h4 class="cart-drawer__item-title">${item.product_title}</h4>
          <div class="cart-drawer__item-price">${this.formatMoney(item.final_price)}</div>
          
          <div class="cart-drawer__item-actions">
            <div class="cart-drawer__item-quantity">
              <button type="button" class="cart-drawer__quantity-btn cart-drawer__quantity-minus" 
                      data-variant-id="${item.variant_id}" 
                      data-current-quantity="${item.quantity}"
                      aria-label="减少数量">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M4 8H12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
              
              <span class="cart-drawer__quantity-display">${item.quantity}</span>
              
              <button type="button" class="cart-drawer__quantity-btn cart-drawer__quantity-plus" 
                      data-variant-id="${item.variant_id}" 
                      data-current-quantity="${item.quantity}"
                      aria-label="增加数量">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M8 4V12M4 8H12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            
            <button type="button" class="cart-drawer__item-remove" 
                    data-variant-id="${item.variant_id}"
                    aria-label="移除商品">
              Remove
            </button>
          </div>
        </div>
      </div>
    `
      )
      .join("");
  }

  async updateQuantity(variantId, quantity) {
    if (this.isLoading) return;

    console.log("Updating quantity:", { variantId, quantity });
    this.isLoading = true;

    // 添加加载状态
    if (this.drawer) {
      this.drawer.classList.add("loading");
    }

    try {
      // 使用更可靠的请求方式
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", quantity);

      const response = await fetch("/cart/change.js", {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();
      console.log("Quantity updated successfully:", cart);

      this.currentCart = cart;

      // 更新抽屉内容
      await this.updateDrawerContent(cart);

      // 更新页面上的购物车数量
      this.updateCartCount(cart.item_count);

      // 触发自定义事件
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: cart }));
    } catch (error) {
      console.error("Error updating quantity:", error);
      this.showError("更新数量失败，请重试。");

      // 刷新购物车以恢复正确状态
      this.refreshCart();
    } finally {
      this.isLoading = false;
      if (this.drawer) {
        this.drawer.classList.remove("loading");
      }
    }
  }

  async removeItem(variantId) {
    console.log("Removing item:", variantId);
    return this.updateQuantity(variantId, 0);
  }

  async updateNote(note) {
    try {
      console.log("Updating cart note:", note);

      const formData = new FormData();
      formData.append("note", note);

      const response = await fetch("/cart/update.js", {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log("Cart note updated successfully");
    } catch (error) {
      console.error("Error updating note:", error);
    }
  }

  updateCartCount(count) {
    console.log("Updating cart count:", count);

    // 更新所有带有 data-cart-count 属性的元素
    const cartCountElements = document.querySelectorAll("[data-cart-count]");
    cartCountElements.forEach((element) => {
      if (count !== undefined) {
        element.textContent = count;
        if (count === 0) {
          element.style.display = "none";
        } else {
          element.style.display = "";
        }
      }
    });

    // 更新 cart-count 元素
    const cartCounts = document.querySelectorAll("cart-count");
    cartCounts.forEach((element) => {
      if (count !== undefined) {
        element.textContent = count;
        if (count === 0) {
          element.setAttribute("hidden", "");
        } else {
          element.removeAttribute("hidden");
        }
      }
    });

    // 更新购物车图标上的数量显示
    const cartBubbles = document.querySelectorAll(".cart-count, .count");
    cartBubbles.forEach((element) => {
      if (count !== undefined) {
        element.textContent = count;
        if (count === 0) {
          element.style.display = "none";
        } else {
          element.style.display = "";
        }
      }
    });

    // 更新侧边栏中的数量显示
    const drawerCounts = document.querySelectorAll(".cart-drawer__count");
    drawerCounts.forEach((element) => {
      if (count !== undefined) {
        element.textContent = count;
      }
    });
  }

  formatMoney(cents) {
    // 简单的货币格式化，您可以根据需要调整
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  showError(message) {
    console.error("Showing error:", message);

    // 移除现有的错误提示
    const existingError = document.querySelector(".cart-drawer-error");
    if (existingError) {
      existingError.remove();
    }

    // 创建新的错误提示
    const errorDiv = document.createElement("div");
    errorDiv.className = "cart-drawer-error";
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc2626;
      color: white;
      padding: 12px 16px;
      border-radius: 6px;
      z-index: 10001;
      font-size: 14px;
      max-width: 300px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;
    errorDiv.textContent = message;

    document.body.appendChild(errorDiv);

    // 3秒后自动移除
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove();
      }
    }, 3000);
  }

  // 公共方法：添加商品到购物车并打开抽屉
  async addItem(variantId, quantity = 1, properties = {}, productHandle = null) {
    if (this.isLoading) return;

    console.log("Adding item to cart:", { variantId, quantity, properties });
    this.isLoading = true;

    try {
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", quantity);

      // 添加商品属性
      Object.keys(properties).forEach((key) => {
        formData.append(`properties[${key}]`, properties[key]);
      });

      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "添加商品到购物车失败");
      }

      console.log("Item added successfully");

      // 刷新购物车数据
      await this.refreshCart();

      // 确保在刷新购物车数据后，立即更新购物车计数
      if (this.currentCart) {
        this.updateCartCount(this.currentCart.item_count);
      }

      // 打开抽屉（只有在购物车不为空时）
      if (this.currentCart && this.currentCart.item_count > 0) {
        await this.open();
      }

      // 触发自定义事件
      document.dispatchEvent(
        new CustomEvent("cart:item-added", {
          detail: { variantId, quantity, properties },
        })
      );

      // 将商品添加到最近浏览列表
      if (window.recentlyViewed && productHandle) {
        window.recentlyViewed.addProduct(productHandle);
      }
    } catch (error) {
      console.error("Error adding item to cart:", error);
      this.showError(error.message || "添加商品到购物车失败");
    } finally {
      this.isLoading = false;
    }
  }
}

// 当DOM加载完成时初始化购物车抽屉
document.addEventListener("DOMContentLoaded", () => {
  console.log("DOM loaded, initializing CartDrawer...");
  window.cartDrawer = new CartDrawer();
});

// 暴露全局函数以便于集成
window.openCartDrawer = () => {
  console.log("openCartDrawer called");
  if (window.cartDrawer) {
    window.cartDrawer.handleCartClick();
  } else {
    console.error("CartDrawer not initialized");
  }
};

window.closeCartDrawer = () => {
  console.log("closeCartDrawer called");
  if (window.cartDrawer) {
    window.cartDrawer.close();
  }
};

window.addToCartDrawer = (variantId, quantity = 1, properties = {}, productHandle = null) => {
  console.log("addToCartDrawer called:", { variantId, quantity, properties, productHandle });
  if (window.cartDrawer) {
    return window.cartDrawer.addItem(variantId, quantity, properties, productHandle);
  } else {
    console.error("CartDrawer not initialized");
  }
};